import {
  useConfirmMfaSetup,
  type MfaSetupConfirmation,
} from '@/http/queries/useConfirmMfaSetup';
import { useStartTotpSetup } from '@/http/queries/useStartTotpSetup';
import { T } from '@/i18n/T';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import CopyToClipboard from '@components/Button/CopyToClipboard';
import LoadingButton from '@components/Button/LoadingButton';
import { Button, CircularProgress, Typography } from '@eye-able/ui';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import CodeInput from './CodeInput';
import MfaErrorAlert, { type MfaErrorOverrides } from './MfaErrorAlert';

interface TotpSetupProps {
  /** From the login's enrol step, or from the proof route in the profile. */
  challenge: string;
  onConfirmed: (result: MfaSetupConfirmation) => void;
  onBack: () => void;
  /** From the profile a dead challenge means the setup expired, not the login. */
  errorOverrides?: MfaErrorOverrides;
}

/** Asks the API for a secret once, however often React mounts the effect. */
function useTotpSecret(challenge: string) {
  const setup = useStartTotpSetup();
  const { mutate } = setup;
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    mutate({ challenge });
  }, [mutate, challenge]);
  return setup;
}

/** Shows the secret as QR code and text, then takes the app's first code. */
function TotpSetup({
  challenge,
  onConfirmed,
  onBack,
  errorOverrides,
}: TotpSetupProps) {
  const { t } = useInlineTranslation();
  const setup = useTotpSecret(challenge);
  const confirm = useConfirmMfaSetup();
  const [code, setCode] = useState('');
  const codeId = useId();
  const errorId = useId();
  const ready = code.length === 6;

  // Takes the code as an argument: on auto-submit the state is one render behind.
  const submit = (value: string) => {
    if (value.length < 6 || confirm.isPending) return;
    confirm.mutate(
      { challenge, type: 'totp', code: value },
      { onSuccess: onConfirmed, onError: () => setCode('') },
    );
  };
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    submit(code);
  };

  if (setup.isPending || (!setup.data && !setup.error)) {
    return (
      <div className='eye:flex eye:justify-center eye:py-m'>
        <CircularProgress aria-label={t('loading')} />
      </div>
    );
  }
  if (!setup.data) {
    return (
      <div className='eye:flex eye:flex-col eye:gap-16'>
        <MfaErrorAlert error={setup.error} overrides={errorOverrides} />
        <Button type='button' variant='outline' onClick={onBack}>
          {t('back')}
        </Button>
      </div>
    );
  }

  const { secret, otpauthUri } = setup.data;
  return (
    <form onSubmit={onSubmit} className='eye:flex eye:flex-col eye:gap-16'>
      <Typography>
        <T k='mfaTotpScanHint' />
      </Typography>
      <div className='eye:flex eye:flex-col eye:items-start eye:gap-16 eye:sm:flex-row'>
        <div className='eye:shrink-0 eye:rounded-md eye:border eye:border-grey-20 eye:bg-white eye:p-4xs'>
          <QRCodeSVG
            value={otpauthUri}
            size={152}
            role='img'
            aria-label={t('mfaTotpQrCode')}
          />
        </div>
        <div className='eye:flex eye:min-w-0 eye:flex-1 eye:flex-col eye:gap-8'>
          <div className='eye:text-14 eye:leading-[20px] eye:font-medium'>
            <T k='mfaTotpSecretHint' />
          </div>
          <div className='eye:flex eye:items-center eye:gap-3xs eye:rounded-md eye:border eye:border-dashed eye:border-grey-40 eye:px-3xs eye:py-4xs'>
            <span className='eye:flex-1 eye:font-mono eye:text-12 eye:tracking-wider eye:break-all'>
              {secret.replace(/(.{4})/g, '$1 ').trim()}
            </span>
            <CopyToClipboard text={secret} label={t('mfaCopySecret')} />
          </div>
        </div>
      </div>
      <div className='eye:flex eye:flex-col eye:gap-3xs'>
        <Typography tag='label' variant='body-m-highlight' htmlFor={codeId}>
          <T k='mfaEnterAppCode' />
        </Typography>
        <CodeInput
          id={codeId}
          value={code}
          onChange={(value) => {
            setCode(value);
            if (confirm.error) confirm.reset();
          }}
          onComplete={submit}
          describedBy={confirm.error ? errorId : undefined}
          invalid={!!confirm.error}
          disabled={confirm.isPending}
          autoFocus
        />
      </div>
      <MfaErrorAlert
        id={errorId}
        error={confirm.error}
        overrides={errorOverrides}
      />
      <div className='eye:flex eye:justify-end eye:gap-2xs'>
        <Button
          type='button'
          variant='outline'
          onClick={onBack}
          disabled={confirm.isPending}
        >
          {t('back')}
        </Button>
        <LoadingButton
          type='submit'
          variant='yellow'
          loading={confirm.isPending}
          disabled={!ready}
        >
          {t('mfaActivate')}
        </LoadingButton>
      </div>
    </form>
  );
}

export default TotpSetup;
